import dayjs from 'dayjs'
import { useRouter } from 'next/router'
import { createContext, useContext, useEffect, useMemo } from 'react'

import Intercom from '../components/Intercom'
import { posthogEvent } from '../lib/helpers'
import { useFirebase } from './FirebaseProvider'

const restrictWixUserTime = 1748304000000

const AuthContext = createContext()

function AuthProvider({
  children,
  userData,
  authUser,
  currentPlan,
  viewCountAndLimit
}) {
  const router = useRouter()

  const { firebase } = useFirebase()

  const trackPosthogEvent = async () => {
    if (!userData || !userData?.userId) return

    if (userData?.trackingEvents?.signedUp) return

    await firebase.updateUserData({
      ...userData,
      trackingEvents: { ...userData?.trackingEvents, signedUp: true }
    })
    posthogEvent({ eventType: 'capture', key: 'sign_up' })
  }

  const initializePosthog = async () => {
    posthogEvent({
      eventType: 'identify',
      key: userData.userId,
      value: {
        email: userData?.email ?? '',
        plan: userData?.platform?.currentPlan.toLowerCase() ?? 'free',
        platform: userData?.platform?.name.toLowerCase(),
        platformPlan: userData?.platformPlan ?? '',
        staffAccount: userData?.staffAccount === true
      }
    })
    await trackPosthogEvent()
  }

  const restrictWixFreeUser = useMemo(() => {
    const { platform, createdDtm } = userData ?? {}
    const currentPlan = platform?.currentPlan?.toLowerCase()
    const platformName = platform?.name?.toLowerCase()
    const pathname = router.pathname
    const userSignupTime = firebase.getCreatedAt(createdDtm)

    const shouldRedirect =
      platformName === 'wix' &&
      currentPlan === 'free' &&
      dayjs(userSignupTime).isAfter(restrictWixUserTime) &&
      pathname !== '/wix/pricing'

    return shouldRedirect
  }, [userData, router.pathname])

  useEffect(() => {
    initializePosthog()
  }, [router.query.host, userData])

  if (restrictWixFreeUser) {
    router.push('/wix/pricing')
    return null
  }

  return (
    <AuthContext.Provider
      value={{ userData, authUser, currentPlan, viewCountAndLimit }}
    >
      <Intercom userData={userData}>{children}</Intercom>
    </AuthContext.Provider>
  )
}

const useAuth = () => useContext(AuthContext)

export { AuthProvider, useAuth }
